import { useState, useEffect, useCallback } from 'react';
import { View, Text, Pressable, ActivityIndicator } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useRouter } from 'expo-router';
import {
  ChevronLeft,
  FolderOpen,
  Cloud,
  CheckCircle,
  ExternalLink,
  Shield,
  FileText,
  Loader,
} from 'lucide-react-native';
import Animated, { FadeInDown, FadeInUp, ZoomIn } from 'react-native-reanimated';
import { handleError, reportError } from '@/lib/errors';
import { useSystemThemeColors } from '@/lib/useThemeColors';
import { useProfileStore } from '@/lib/state/profile-store';
import { SkipButton } from '@/components/SkipButton';
import AnimatedPressable from '@/components/AnimatedPressable';
import { useAlert } from '@/components/AlertDialog';
import {
  useGoogleAuth,
  exchangeCodeForTokens,
  getDriveUserInfo,
  useGoogleDriveStore,
  listFiles as listGoogleDriveFiles,
} from '@/lib/google-drive';
import {
  useDropboxAuth,
  exchangeDropboxCodeForTokens,
  getDropboxUserInfo,
  useDropboxStore,
  listDropboxFolder,
} from '@/lib/dropbox';
import { FolderPicker } from '@/components/google-drive/FolderPicker';
import type { GoogleDriveFolder } from '@/lib/google-drive';
import {
  indexDataRoomDocuments,
  googleDriveFileToIndexable,
  dropboxFileToIndexable,
} from '@/lib/dataroom/document-indexer';
import { getAIScore } from '@/lib/supabase/edge-functions';
import { useAuth } from '@/lib/supabase/auth';
import { useUpdateDataRoomConnection } from '@/lib/supabase/api';
import { profileToScoreInput } from '@/lib/scoring/ai-scoring';
import { track } from '@/lib/analytics';
import { useTrackScreen } from '@/lib/analytics';
import { useAiConsentGate } from '@/lib/consent/ai-consent';

type ConnectionState = 'idle' | 'connecting' | 'authenticated' | 'selecting_folder' | 'indexing' | 'success';
type Provider = 'google' | 'dropbox' | 'docsend';

// Google Drive blue
const GOOGLE_BLUE = '#4285F4';
// Dropbox blue
const DROPBOX_BLUE = '#0061FF';
// DocSend orange
const DOCSEND_ORANGE = '#FF7A45';

export default function DataRoomConnectScreen() {
  useTrackScreen('DataRoomConnect');
  const router = useRouter();
  const colors = useSystemThemeColors();
  const startupProfile = useProfileStore((s) => s.startupProfile);
  const setStartupProfile = useProfileStore((s) => s.setStartupProfile);
  const { user } = useAuth();
  const { error: showError, show } = useAlert();
  const updateDataRoomConnection = useUpdateDataRoomConnection();
  // Connecting a data room both indexes its documents (which can trigger
  // background team extraction, `team_extraction`) and updates the score
  // (`ai_scoring`) — one sheet covers both (D3 addendum "Change from D2
  // review").
  //
  // `onDecline` still reaches the success state: the CONNECTION (persisted by
  // `finishConnection`) is not an AI feature and must not be conditioned on
  // AI-processing consent (GDPR Art. 7(4)) — only the indexing/scoring bonus
  // on top of it is. Reads `startupProfile` fresh at decline time (both
  // handlers below already wrote `dataRoomProvider` / `dataRoomFolderId` to
  // it before opening the sheet), not a value captured when the gate was
  // declared.
  const { runWithConsent: runIndexingWithConsent, sheet: indexingConsentSheet } = useAiConsentGate(
    ['ai_scoring', 'team_extraction'],
    {
      onDecline: () => {
        if (startupProfile?.dataRoomProvider) {
          finishConnection(startupProfile.dataRoomProvider, startupProfile.dataRoomFolderId ?? '');
        }
      },
    },
  );

  const [state, setState] = useState<ConnectionState>('idle');
  const [selectedProvider, setSelectedProvider] = useState<Provider | null>(null);
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [showFolderPicker, setShowFolderPicker] = useState(false);
  const [indexingProgress, setIndexingProgress] = useState<string>('');

  // Google Drive store
  const setGoogleConnectionState = useGoogleDriveStore((s) => s.setConnectionState);
  const setGoogleConnectedEmail = useGoogleDriveStore((s) => s.setConnectedEmail);
  const setGoogleSelectedFolder = useGoogleDriveStore((s) => s.setSelectedFolder);
  const googleConnectionState = useGoogleDriveStore((s) => s.connectionState);

  // Google OAuth hook
  const { request, response, promptAsync, redirectUri, isConfigured: isGoogleOAuthConfigured } = useGoogleAuth();

  // Dropbox store
  const setDropboxConnectionState = useDropboxStore((s) => s.setConnectionState);
  const setDropboxConnectedUser = useDropboxStore((s) => s.setConnectedUser);

  // Dropbox OAuth hook
  const {
    request: dropboxRequest,
    response: dropboxResponse,
    promptAsync: dropboxPromptAsync,
    redirectUri: dropboxRedirectUri,
    isConfigured: isDropboxOAuthConfigured,
  } = useDropboxAuth();

  const isConnected = startupProfile?.dataRoomConnected ?? false;
  const connectedProvider = startupProfile?.dataRoomProvider;

  /**
   * Persist the connection and reach the success state. NOT an AI action —
   * unconditional regardless of whatever the AI-processing consent decision
   * below turns out to be (GDPR Art. 7(4): a non-AI feature must not be
   * conditioned on AI-processing consent). Called either immediately (the
   * founder declined AI processing) or from `indexAndScore`'s `finally`
   * (accepted, whether or not the AI work itself succeeded).
   */
  const finishConnection = useCallback(
    (provider: Provider, folderId: string) => {
      if (!startupProfile) return;
      // Persist the connection to the DB so it survives reload and is visible
      // server-side (dd-chat / investor access). The caller already updated the
      // local store; this is the durable write. Reported but non-blocking — a
      // persist failure shouldn't stop the flow.
      if (user && provider !== 'docsend') {
        updateDataRoomConnection.mutate(
          { connected: true, provider, folderId: folderId || null },
          { onError: (err) => reportError(err, { context: 'DataRoomConnect.persistConnection', silent: true }) },
        );
      }
      track('data_room_connected', { provider });
      setState('success');
    },
    [startupProfile, user, updateDataRoomConnection]
  );

  /**
   * The AI-gated half: index documents (RAG / team extraction) and re-score.
   * Best-effort and silent on failure, same as before this fix — the
   * `finally` is what guarantees `finishConnection` still runs, so an
   * indexing/scoring failure never strands the screen in "indexing".
   */
  const indexAndScore = useCallback(
    async (provider: Provider, folderId: string) => {
      if (!startupProfile) return;

      setState('indexing');
      setIndexingProgress('Fetching documents...');

      try {
        // Fetch files from data room
        let files: any[] = [];

        if (provider === 'google') {
          const { files: driveFiles } = await listGoogleDriveFiles(folderId);
          files = driveFiles
            .filter((f: any) => f.mimeType !== 'application/vnd.google-apps.folder')
            .map((f: any) => googleDriveFileToIndexable(f));
        } else {
          const { files: dropboxFiles } = await listDropboxFolder(folderId);
          files = dropboxFiles.map((f: any) => dropboxFileToIndexable(f));
        }

        if (files.length > 0) {
          setIndexingProgress(`Indexing ${files.length} documents...`);

          // Index documents for RAG
          await indexDataRoomDocuments(files, startupProfile.id, 2);
        }

        // Update score with data room connected bonus
        setIndexingProgress('Updating investment readiness score...');

        try {
          const scoreInput = {
            name: startupProfile.name,
            // JUSTIFIED as any: profile stage type doesn't match score API's expected union
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            stage: startupProfile.stage as any,
            industry: startupProfile.industry,
            profileData: {
              ...startupProfile,
              dataRoomConnected: true,
              dataRoomProvider: provider,
            },
          };

          const scoreResponse = await getAIScore({
            input: scoreInput,
            includeSubScores: true,
          });

          if (scoreResponse.data?.success && scoreResponse.data.data) {
            // Store the new score - could update the profile here if needed
          }
        } catch (scoreError) {
          reportError(scoreError, { context: 'DataRoomConnect.scoreUpdate', silent: true });
        }
      } catch (error) {
        reportError(error, { context: 'DataRoomConnect.handleFilesSelected.indexing', level: 'warning', silent: true });
        // Indexing failure shouldn't block the flow — `finishConnection` below runs regardless.
      } finally {
        finishConnection(provider, folderId);
      }
    },
    [startupProfile, finishConnection]
  );

  /**
   * Handle folder selection from picker
   */
  const handleFolderSelected = useCallback(
    async (folder: GoogleDriveFolder) => {
      if (!startupProfile || !selectedProvider) return;

      setShowFolderPicker(false);
      const provider = selectedProvider;
      const folderId = folder.id;

      // Update startup profile with folder ID
      setStartupProfile({
        ...startupProfile,
        dataRoomConnected: true,
        dataRoomProvider: provider,
        dataRoomFolderId: folderId,
      });

      // Also update the store's selected folder
      if (provider === 'google') {
        setGoogleSelectedFolder(folder);
      }

      // Index documents and update score — gated on explicit AI-processing
      // consent (indexing can trigger team extraction; scoring calls
      // ai-score). Declining reaches 'success' anyway, via the gate's
      // `onDecline` below — the CONNECTION is not conditioned on AI consent,
      // only the indexing/scoring bonus on top of it.
      runIndexingWithConsent(() => {
        void indexAndScore(provider, folderId);
      });
    },
    [startupProfile, selectedProvider, setStartupProfile, indexAndScore, runIndexingWithConsent]
  );

  /**
   * Use root folder as default (for quick setup)
   */
  const handleUseRootFolder = useCallback(async () => {
    if (!startupProfile || !selectedProvider) return;

    const provider = selectedProvider;
    const rootFolderId = provider === 'google' ? 'root' : '';

    // Update startup profile
    setStartupProfile({
      ...startupProfile,
      dataRoomConnected: true,
      dataRoomProvider: provider,
      dataRoomFolderId: rootFolderId,
    });

    // Index documents and update score — gated on explicit AI-processing
    // consent, same as the folder-picker path above.
    runIndexingWithConsent(() => {
      void indexAndScore(provider, rootFolderId);
    });
  }, [startupProfile, selectedProvider, setStartupProfile, indexAndScore, runIndexingWithConsent]);

  // Handle Google OAuth response
  useEffect(() => {
    if (response?.type === 'success' && response.params.code) {
      handleGoogleAuthSuccess(response.params.code);
    } else if (response?.type === 'error') {
      setIsAuthenticating(false);
      setGoogleConnectionState('error');
      reportError(response.error, { context: 'DataRoomConnect.googleOAuth', level: 'warning', silent: true });
      showError('Authentication Failed', 'Failed to connect to Google Drive. Please try again.');
      setState('idle');
      setSelectedProvider(null);
    } else if (response?.type === 'cancel' || response?.type === 'dismiss') {
      setIsAuthenticating(false);
      setState('idle');
      setSelectedProvider(null);
    }
  }, [response]);

  // Handle Dropbox OAuth response
  useEffect(() => {
    if (dropboxResponse?.type === 'success' && dropboxResponse.params.code) {
      handleDropboxAuthSuccess(dropboxResponse.params.code);
    } else if (dropboxResponse?.type === 'error') {
      setIsAuthenticating(false);
      setDropboxConnectionState('error');
      reportError(dropboxResponse.error, { context: 'DataRoomConnect.dropboxOAuth', level: 'warning', silent: true });
      showError('Authentication Failed', 'Failed to connect to Dropbox. Please try again.');
      setState('idle');
      setSelectedProvider(null);
    } else if (dropboxResponse?.type === 'cancel' || dropboxResponse?.type === 'dismiss') {
      setIsAuthenticating(false);
      setState('idle');
      setSelectedProvider(null);
    }
  }, [dropboxResponse]);

  const handleGoogleAuthSuccess = async (code: string) => {
    try {
      if (!request?.codeVerifier) {
        throw new Error('Missing code verifier');
      }

      // Exchange code for tokens
      await exchangeCodeForTokens(code, request.codeVerifier, redirectUri);

      // Get user info
      const userInfo = await getDriveUserInfo();

      // Update Google Drive store
      setGoogleConnectionState('connected');
      setGoogleConnectedEmail(userInfo?.email || null);

      setIsAuthenticating(false);
      setState('authenticated');

      // Show folder selection options
      show({
        title: 'Select Data Room Folder',
        message: 'Choose which folder to use as your data room.',
        buttons: [
          {
            text: 'Use My Drive (Root)',
            onPress: handleUseRootFolder,
          },
          {
            text: 'Choose Folder',
            onPress: () => setShowFolderPicker(true),
          },
        ],
      });
    } catch (error) {
      handleError(error, { context: 'DataRoomConnect.handleGoogleAuthSuccess' });
      setGoogleConnectionState('error');
      setIsAuthenticating(false);
      showError(
        'Connection Failed',
        error instanceof Error ? error.message : 'Failed to connect to Google Drive'
      );
      setState('idle');
      setSelectedProvider(null);
    }
  };

  const handleDropboxAuthSuccess = async (code: string) => {
    try {
      if (!dropboxRequest?.codeVerifier) {
        throw new Error('Missing code verifier');
      }

      // Exchange code for tokens
      await exchangeDropboxCodeForTokens(code, dropboxRequest.codeVerifier, dropboxRedirectUri);

      // Get user info
      const userInfo = await getDropboxUserInfo();

      // Update Dropbox store
      setDropboxConnectionState('connected');
      setDropboxConnectedUser(userInfo?.email || null, userInfo?.name || null);

      setIsAuthenticating(false);
      setState('authenticated');

      // For Dropbox, use root by default (folder picker not implemented for Dropbox)
      // Could add a Dropbox folder picker later
      show({
        title: 'Dropbox Connected',
        message: 'Your Dropbox root folder will be used as your data room. Documents will be indexed automatically.',
        buttons: [
          {
            text: 'Continue',
            onPress: handleUseRootFolder,
          },
        ],
      });
    } catch (error) {
      handleError(error, { context: 'DataRoomConnect.handleDropboxAuthSuccess' });
      setDropboxConnectionState('error');
      setIsAuthenticating(false);
      showError(
        'Connection Failed',
        error instanceof Error ? error.message : 'Failed to connect to Dropbox'
      );
      setState('idle');
      setSelectedProvider(null);
    }
  };

  const handleConnect = async (provider: Provider) => {
    setSelectedProvider(provider);
    setState('connecting');

    if (provider === 'google') {
      // Check if Google OAuth is configured
      if (!isGoogleOAuthConfigured) {
        show({
          title: 'Not Configured',
          message: 'Google Drive integration is not configured. Please add EXPO_PUBLIC_GOOGLE_WEB_CLIENT_ID to your environment.',
          buttons: [{ text: 'OK', onPress: () => { setState('idle'); setSelectedProvider(null); } }],
        });
        return;
      }

      // Start real OAuth flow
      setIsAuthenticating(true);
      setGoogleConnectionState('connecting');

      try {
        await promptAsync();
      } catch (error) {
        setIsAuthenticating(false);
        setGoogleConnectionState('error');
        setState('idle');
        setSelectedProvider(null);
        showError('Error', 'Failed to start authentication');
      }
    } else if (provider === 'dropbox') {
      // Real Dropbox OAuth flow
      if (!isDropboxOAuthConfigured) {
        show({
          title: 'Not Configured',
          message: 'Dropbox integration is not configured. Please add EXPO_PUBLIC_DROPBOX_APP_KEY to your environment.',
          buttons: [{ text: 'OK', onPress: () => { setState('idle'); setSelectedProvider(null); } }],
        });
        return;
      }

      // Start real OAuth flow
      setIsAuthenticating(true);
      setDropboxConnectionState('connecting');

      try {
        await dropboxPromptAsync();
      } catch (error) {
        setIsAuthenticating(false);
        setDropboxConnectionState('error');
        setState('idle');
        setSelectedProvider(null);
        showError('Error', 'Failed to start authentication');
      }
    } else {
      // DocSend — no public OAuth API; user pastes a sharing link
      // TODO: replace with real DocSend integration when available.
      show({
        title: 'DocSend (Beta)',
        message: 'DocSend integration is in early access. Paste a DocSend space link in your settings to connect.',
        buttons: [{ text: 'OK', onPress: () => { setState('idle'); setSelectedProvider(null); } }],
      });
    }
  };

  const handleContinue = () => {
    router.push('/(onboarding)/complete');
  };

  const handleBack = () => {
    router.back();
  };

  return (
    <View className="flex-1" style={{ backgroundColor: colors.surface.secondary }}>
      <SafeAreaView edges={['top', 'bottom']} className="flex-1">
        {/* Header */}
        <Animated.View
          entering={FadeInDown.delay(100).duration(500)}
          className="px-5 pt-4 pb-4"
        >
          <View className="flex-row items-center justify-between">
            <Pressable
              onPress={handleBack}
              className="w-10 h-10 rounded-full items-center justify-center"
              style={{ backgroundColor: colors.surface.primary }}
            >
              <ChevronLeft size={24} color={colors.text.primary} />
            </Pressable>
            <View className="flex-row gap-2">
              <View
                className="h-1 w-8 rounded-full"
                style={{ backgroundColor: colors.primary[500] }}
              />
              <View
                className="h-1 w-8 rounded-full"
                style={{
                  backgroundColor:
                    state === 'success' || isConnected
                      ? colors.primary[500]
                      : colors.border.light,
                }}
              />
              <View
                className="h-1 w-8 rounded-full"
                style={{ backgroundColor: colors.border.light }}
              />
            </View>
            <View className="w-10" />
          </View>
        </Animated.View>

        {/* Content */}
        <View className="flex-1 px-6 justify-center">
          {state === 'indexing' ? (
            // Indexing State
            <Animated.View
              entering={ZoomIn.delay(200).duration(600).springify()}
              className="items-center"
            >
              <View
                className="w-24 h-24 rounded-full items-center justify-center mb-6"
                style={{ backgroundColor: colors.primary[50] }}
              >
                <ActivityIndicator size="large" color={colors.primary[600]} />
              </View>

              <Text
                style={{ color: colors.text.primary }}
                className="text-2xl font-bold text-center mb-2"
              >
                Setting Up Data Room
              </Text>
              <Text
                style={{ color: colors.text.secondary }}
                className="text-base text-center mb-6"
              >
                {indexingProgress || 'Processing...'}
              </Text>

              <View
                className="p-4 rounded-xl w-full"
                style={{ backgroundColor: colors.surface.primary }}
              >
                <View className="flex-row items-center gap-3">
                  <Loader size={20} color={colors.primary[600]} />
                  <View className="flex-1">
                    <Text
                      style={{ color: colors.text.tertiary }}
                      className="text-sm"
                    >
                      This may take a moment. Your documents are being prepared for AI-powered analysis.
                    </Text>
                  </View>
                </View>
              </View>
            </Animated.View>
          ) : state === 'success' || isConnected ? (
            // Success State
            <Animated.View
              entering={ZoomIn.delay(200).duration(600).springify()}
              className="items-center"
            >
              <View
                className="w-24 h-24 rounded-full items-center justify-center mb-6"
                style={{ backgroundColor: colors.traffic.greenLight }}
              >
                <CheckCircle size={48} color={colors.traffic.green} />
              </View>

              <Text
                style={{ color: colors.text.primary }}
                className="text-2xl font-bold text-center mb-2"
              >
                Data Room Connected!
              </Text>
              <Text
                style={{ color: colors.text.secondary }}
                className="text-base text-center mb-6"
              >
                Your {connectedProvider === 'google' ? 'Google Drive' : connectedProvider === 'dropbox' ? 'Dropbox' : 'DocSend'} is
                now linked as your data room
              </Text>

              <View
                className="p-4 rounded-xl w-full"
                style={{ backgroundColor: colors.surface.primary }}
              >
                <View className="flex-row items-center gap-3 mb-3">
                  <View
                    className="w-10 h-10 rounded-full items-center justify-center"
                    style={{
                      backgroundColor:
                        connectedProvider === 'google'
                          ? '#E8F0FE'
                          : connectedProvider === 'dropbox'
                            ? '#E6F0FF'
                            : '#FFF1EC',
                    }}
                  >
                    <Cloud
                      size={20}
                      color={
                        connectedProvider === 'google'
                          ? GOOGLE_BLUE
                          : connectedProvider === 'dropbox'
                            ? DROPBOX_BLUE
                            : DOCSEND_ORANGE
                      }
                    />
                  </View>
                  <View className="flex-1">
                    <Text
                      style={{ color: colors.text.primary }}
                      className="font-semibold"
                    >
                      {connectedProvider === 'google' ? 'Google Drive' : connectedProvider === 'dropbox' ? 'Dropbox' : 'DocSend'}
                    </Text>
                    <Text
                      style={{ color: colors.traffic.green }}
                      className="text-xs"
                    >
                      Connected
                    </Text>
                  </View>
                  <CheckCircle size={20} color={colors.traffic.green} />
                </View>

                <View className="flex-row items-center gap-2">
                  <FileText size={14} color={colors.text.tertiary} />
                  <Text
                    style={{ color: colors.text.tertiary }}
                    className="text-xs"
                  >
                    Investors will be able to request access to your documents
                  </Text>
                </View>
              </View>
            </Animated.View>
          ) : (
            // Idle/Connecting State
            <Animated.View
              entering={FadeInUp.delay(200).duration(600)}
              className="items-center"
            >
              <View
                className="w-24 h-24 rounded-full items-center justify-center mb-6"
                style={{ backgroundColor: colors.primary[50] }}
              >
                <FolderOpen size={48} color={colors.primary[600]} />
              </View>

              <Text
                style={{ color: colors.text.primary }}
                className="text-2xl font-bold text-center mb-2"
              >
                Connect Your Data Room
              </Text>
              <Text
                style={{ color: colors.text.secondary }}
                className="text-base text-center leading-relaxed px-4 mb-8"
              >
                Link your cloud storage to share documents securely with investors
              </Text>

              {/* Provider Options */}
              <View className="w-full gap-4">
                {/* Google Drive */}
                <AnimatedPressable
                  onPress={() => handleConnect('google')}
                  disabled={state === 'connecting' || isAuthenticating}
                >
                  <View
                    className="flex-row items-center p-4 rounded-xl overflow-hidden"
                    style={{
                      backgroundColor: colors.surface.primary,
                      borderWidth: 2,
                      borderColor:
                        state === 'connecting' && selectedProvider === 'google'
                          ? GOOGLE_BLUE
                          : colors.border.light,
                    }}
                  >
                    <View
                      className="w-12 h-12 rounded-xl items-center justify-center mr-4"
                      style={{ backgroundColor: '#E8F0FE' }}
                    >
                      <Cloud size={24} color={GOOGLE_BLUE} />
                    </View>
                    <View className="flex-1">
                      <Text
                        style={{ color: colors.text.primary }}
                        className="font-semibold text-base"
                      >
                        Google Drive
                      </Text>
                      <Text
                        style={{ color: colors.text.tertiary }}
                        className="text-xs"
                      >
                        Connect your Google Workspace
                      </Text>
                    </View>
                    {state === 'connecting' && selectedProvider === 'google' && isAuthenticating ? (
                      <ActivityIndicator color={GOOGLE_BLUE} />
                    ) : (
                      <ExternalLink size={18} color={colors.text.tertiary} />
                    )}
                  </View>
                </AnimatedPressable>

                {/* Dropbox */}
                <AnimatedPressable
                  onPress={() => handleConnect('dropbox')}
                  disabled={state === 'connecting' || isAuthenticating}
                >
                  <View
                    className="flex-row items-center p-4 rounded-xl"
                    style={{
                      backgroundColor: colors.surface.primary,
                      borderWidth: 2,
                      borderColor:
                        state === 'connecting' && selectedProvider === 'dropbox'
                          ? DROPBOX_BLUE
                          : colors.border.light,
                    }}
                  >
                    <View
                      className="w-12 h-12 rounded-xl items-center justify-center mr-4"
                      style={{ backgroundColor: '#E6F0FF' }}
                    >
                      <Cloud size={24} color={DROPBOX_BLUE} />
                    </View>
                    <View className="flex-1">
                      <Text
                        style={{ color: colors.text.primary }}
                        className="font-semibold text-base"
                      >
                        Dropbox
                      </Text>
                      <Text
                        style={{ color: colors.text.tertiary }}
                        className="text-xs"
                      >
                        Connect your Dropbox account
                      </Text>
                    </View>
                    {state === 'connecting' && selectedProvider === 'dropbox' && isAuthenticating ? (
                      <ActivityIndicator color={DROPBOX_BLUE} />
                    ) : (
                      <ExternalLink size={18} color={colors.text.tertiary} />
                    )}
                  </View>
                </AnimatedPressable>

                {/* DocSend */}
                <AnimatedPressable
                  onPress={() => handleConnect('docsend')}
                  disabled={state === 'connecting' || isAuthenticating}
                  style={{ marginTop: 12 }}
                >
                  <View
                    className="flex-row items-center p-4 rounded-xl"
                    style={{
                      backgroundColor: colors.surface.primary,
                      borderWidth: 2,
                      borderColor:
                        state === 'connecting' && selectedProvider === 'docsend'
                          ? DOCSEND_ORANGE
                          : colors.border.light,
                    }}
                  >
                    <View
                      className="w-12 h-12 rounded-xl items-center justify-center mr-4"
                      style={{ backgroundColor: '#FFF1EC' }}
                    >
                      <Cloud size={24} color={DOCSEND_ORANGE} />
                    </View>
                    <View className="flex-1">
                      <Text
                        style={{ color: colors.text.primary }}
                        className="font-semibold text-base"
                      >
                        DocSend
                      </Text>
                      <Text
                        style={{ color: colors.text.tertiary }}
                        className="text-xs"
                      >
                        Connect your DocSend space
                      </Text>
                    </View>
                    {state === 'connecting' && selectedProvider === 'docsend' && isAuthenticating ? (
                      <ActivityIndicator color={DOCSEND_ORANGE} />
                    ) : (
                      <ExternalLink size={18} color={colors.text.tertiary} />
                    )}
                  </View>
                </AnimatedPressable>
              </View>

              {/* Security Note */}
              <View
                className="mt-6 p-4 rounded-xl w-full"
                style={{ backgroundColor: colors.surface.primary }}
              >
                <View className="flex-row items-center gap-2">
                  <Shield size={16} color={colors.traffic.green} />
                  <Text
                    style={{ color: colors.text.tertiary }}
                    className="text-xs flex-1"
                  >
                    Your files stay in your cloud storage. We only provide
                    controlled access links to approved investors.
                  </Text>
                </View>
              </View>
            </Animated.View>
          )}
        </View>

        {/* Continue Button */}
        {(state === 'success' || isConnected) && (
          <Animated.View
            entering={FadeInUp.delay(400).duration(500)}
            className="px-5 pb-8"
          >
            <AnimatedPressable
              onPress={handleContinue}
              className="rounded-xl py-4 flex-row items-center justify-center gap-2"
              style={{ backgroundColor: colors.primary[600] }}
            >
              <Text
                style={{ color: colors.text.inverse }}
                className="font-semibold text-base"
              >
                Continue
              </Text>
            </AnimatedPressable>
          </Animated.View>
        )}

        {/* Skip Option */}
        {state === 'idle' && !isConnected && (
          <SkipButton
            step="data-room"
            nextRoute="/(onboarding)/complete"
          />
        )}
      </SafeAreaView>

      {/* Google Drive Folder Picker Modal */}
      {selectedProvider === 'google' && (
        <FolderPicker
          visible={showFolderPicker}
          onClose={() => setShowFolderPicker(false)}
          onSelectFolder={handleFolderSelected}
        />
      )}

      {indexingConsentSheet}
    </View>
  );
}
